import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/dist/query/react";
import type {
  BaseQueryFn,
  FetchArgs,
  FetchBaseQueryError,
} from "@reduxjs/toolkit/query";
import { Mutex } from "async-mutex";
import { RootState } from "../../store";
import { getUserTimezone } from "../../utils/formatTime";
import { App } from "../../models";
import { baseQuery } from "./apiSlice";
import { setAppUser } from "../../store/appUserReducer";
import {
  CACHE_KEEP_UNUSED,
  CACHE_STALE_AFTER,
  DEFAULT_LANGUAGE,
} from "../../constants";
const mutex = new Mutex();

const baseQueryWithReauth: BaseQueryFn<
  string | FetchArgs,
  unknown,
  FetchBaseQueryError
> = async (args: any, api: any, extraOptions: any) => {
  // wait if refresh is already happening
  await mutex.waitForUnlock();
  let result = await crmBaseQuery(args, api, extraOptions);
  if (args?.url != "/account/login" && result?.meta?.response?.status === 401) {
    // 🔒 if no refresh in progress → start one
    if (!mutex.isLocked()) {
      const release = await mutex.acquire();

      try {
        // 🔁 call refresh API
        const refreshResult = await baseQuery(
          {
            url: "/account/refresh",
            method: "POST",
          },
          api,
          extraOptions,
        );

        if (refreshResult?.data) {
          // 🔁 retry original request
          result = await crmBaseQuery(args, api, extraOptions);
        } else {
          // refresh failed → logout
          // Refresh failed → session has expired
          api.dispatch(
            setAppUser({
              user: null,
              language: DEFAULT_LANGUAGE,
              darkTheme: false,
            }),
          );

          // Prevent the app from continuing with an expired session
          // window.location.replace("/login");
        }
      } finally {
        // 🔓 release lock
        release();
      }
    } else {
      // 🔁 wait for refresh to finish, then retry
      await mutex.waitForUnlock();

      // Check whether the refresh process logged the user out.
      const state = api.getState() as RootState;

      if (!state?.appUser) {
        return result;
      }

      result = await crmBaseQuery(args, api, extraOptions);
    }
  }
  return result;
};

export const emptySplitCRMApi = createApi({
  reducerPath: "crm-api",
  baseQuery: baseQueryWithReauth,
  keepUnusedDataFor: CACHE_KEEP_UNUSED,
  tagTypes: ["CRM"],
  endpoints: () => ({}),
  refetchOnMountOrArgChange: CACHE_STALE_AFTER,
});

const crmBaseQuery = fetchBaseQuery({
  baseUrl: process.env.REACT_APP_CRM_API_URL,
  credentials: "include",
  prepareHeaders: (headers: any, { getState, endpoint }: any) => {
    headers.set("app", process.env.REACT_APP_APP || App.APP);
    headers.set("Accept", "application/json");
    headers.set("Content-Type", "application/json");
    const state = getState() as RootState;
    if (state?.appUser?.language?.length > 0) {
      headers.set("Accept-Language", state.appUser.language);
    }

    if (state?.appUser?.user?.id) {
      // headers.set("authorization", `Bearer ${state?.appUser.token}`);
      headers.set("client-id", state?.appUser.user?.currentClient?.id);
      // headers.set("parent-id", state?.appUser.user?.clientId);
      // headers.set("user-id", state?.appUser.user?.id);
      headers.set(
        "tz",
        state?.appUser.user?.currentClient?.timezone
          ? state?.appUser.user?.currentClient?.timezone
          : getUserTimezone(),
      );
    } else {
      headers.set("tz", getUserTimezone());
    }
    // headers.set('authorization', `Bearer eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJ1c2VybmFtZSI6IkFkbWluIiwiaWF0IjoxNjYyNTI3MjE3LCJleHAiOjE2NjI2MTM2MTd9.MpvoP2Uy3MnxMNV81cm8-j4yVww6NRuvM2eE_tzFlaw`)
    return headers;
  },
});
